/**
 * Utility functions for handling player positions
 */

// Offensive positions that take precedence over Util
const OFFENSIVE_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH'];

// Special positions that should always be shown
const SPECIAL_POSITIONS = ['IL', 'IL10', 'IL15', 'IL60', 'NA'];

// Pitching positions
const PITCHING_POSITIONS = ['SP', 'RP', 'P'];

/**
 * Filter out "Util" position if player has other offensive positions
 * @param {string} positionsString - Comma-separated positions string
 * @returns {string} - Filtered positions string
 */
export const filterUtilPosition = (positionsString) => {
  if (!positionsString) return '';
  
  // Split and trim positions
  const positions = positionsString.split(',').map(p => p.trim()).filter(p => p);
  
  // Check if player has any offensive positions other than Util
  const hasOtherOffensivePosition = positions.some(pos => 
    OFFENSIVE_POSITIONS.includes(pos)
  );
  
  // If player has other offensive positions, filter out Util
  if (hasOtherOffensivePosition) {
    return positions.filter(pos => pos !== 'Util').join(',');
  }
  
  // Otherwise keep all positions including Util
  return positions.join(',');
};

/**
 * Format positions for display (removes Util if other positions exist)
 * @param {string} positionsString - Comma-separated positions string
 * @returns {string[]} - Array of positions for display
 */
export const formatPositionDisplay = (positionsString) => {
  if (!positionsString) return [];
  
  const filtered = filterUtilPosition(positionsString);
  return filtered.split(',').map(p => p.trim()).filter(p => p);
};

/**
 * Check if player only has Util position (no other offensive positions)
 * @param {string} positionsString - Comma-separated positions string
 * @returns {boolean} - True if player only has Util position
 */
export const hasOnlyUtilPosition = (positionsString) => {
  if (!positionsString) return false;
  
  const positions = positionsString.split(',').map(p => p.trim()).filter(p => p);
  
  // Remove special and pitching positions to check offensive positions
  const offensivePositions = positions.filter(pos => 
    !SPECIAL_POSITIONS.includes(pos) && !PITCHING_POSITIONS.includes(pos)
  );
  
  // Check if Util is the only offensive position
  return offensivePositions.length === 1 && offensivePositions[0] === 'Util';
};

/**
 * Get primary position for display (first non-Util position or Util if only position)
 * @param {string} positionsString - Comma-separated positions string
 * @returns {string} - Primary position for display
 */
export const getPrimaryPosition = (positionsString) => {
  if (!positionsString) return '';
  
  const positions = formatPositionDisplay(positionsString);
  
  // Return first position (which won't be Util unless it's the only one)
  return positions[0] || '';
};

/**
 * Check if position string includes any pitching positions
 * @param {string} positionsString - Comma-separated positions string
 * @returns {boolean} - True if player is a pitcher
 */
export const isPitcher = (positionsString) => {
  if (!positionsString) return false;
  
  const positions = positionsString.split(',').map(p => p.trim());
  return positions.some(pos => PITCHING_POSITIONS.includes(pos));
};

/**
 * Sort positions in standard baseball order
 * @param {string[]} positions - Array of positions
 * @returns {string[]} - Sorted array of positions
 */
export const sortPositions = (positions) => {
  const order = {
    'C': 1, '1B': 2, '2B': 3, '3B': 4, 'SS': 5,
    'LF': 6, 'CF': 7, 'RF': 8, 'OF': 9, 'DH': 10,
    'Util': 11, 'SP': 12, 'RP': 13, 'P': 14,
    'IL': 15, 'IL10': 16, 'IL15': 17, 'IL60': 18, 'NA': 19
  };
  
  return positions.sort((a, b) => {
    const orderA = order[a] || 99;
    const orderB = order[b] || 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.localeCompare(b);
  });
};